<template>
  <v-dialog
    v-model="localModelValue"
    max-width="430"
    persistent
    transition="fade-transition"
    @click:outside="cancel"
  >
    <v-card class="confirmation-modal">
      <div class="modal-content">
        <div class="modal-text">
          <h3 class="modal-title">{{ title }}</h3>
          <p class="modal-description">{{ description || message }}</p>
        </div>
      </div>

      <div class="modal-actions">
        <ActionButton
          v-if="!hideCancel"
          variant="secondary"
          @click="cancel"
          class="modal-button"
          :disabled="loading"
        >
          {{ cancelText }}
        </ActionButton>
        <ActionButton
          :variant="confirmButtonVariant"
          @click="confirm"
          class="modal-button"
          :loading="loading"
          :disabled="loading"
        >
          {{ confirmText }}
        </ActionButton>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, defineProps, defineEmits } from "vue";
import ActionButton from "./ActionButton.vue";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: "Confirmation",
  },
  message: {
    type: String,
    default: "Are you sure you want to proceed?",
  },
  description: {
    type: String,
    default: "",
  },
  confirmText: {
    type: String,
    default: "Confirm",
  },
  cancelText: {
    type: String,
    default: "Cancel",
  },
  confirmColor: {
    type: String,
    default: "error",
  },
  variant: {
    type: String,
    default: "default",
    validator: (value) => ["default", "danger"].includes(value),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  hideCancel: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "confirm", "cancel"]);

const localModelValue = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const confirmButtonVariant = computed(() => {
  return props.variant === "danger" ? "danger" : "primary";
});

const confirm = () => {
  emit("confirm");
};

const cancel = () => {
  emit("cancel");
  localModelValue.value = false;
};
</script>
<style scoped>
.confirmation-modal {
  background: white;
  border-radius: 12px !important;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  box-shadow: none;
  border: none;
  will-change: transform, opacity;
  backface-visibility: hidden;
  transform: translateZ(0);
  position: relative;
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.fade-enter-from {
  opacity: 0;
  transform: scale(0.95);
}

.fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
  transform: scale(1);
}

:deep(.v-dialog) {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

:deep(.v-overlay__content) {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.modal-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
}

.modal-text {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.modal-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-big);
  line-height: 24px;
  color: var(--black-body-text);
  margin: 0;
  letter-spacing: 0.024px;
}

.modal-description {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: var(--neutral-700);
  margin: 0;
  letter-spacing: 0.08px;
}

.modal-actions {
  display: flex;
  gap: 8px;
  width: 100%;
}

.modal-button {
  flex: 1;
  height: 56px;
}

@media (max-width: 768px) {
  .confirmation-modal {
    padding: 20px;
    gap: 24px;
  }

  .modal-actions {
    gap: 12px;
  }

  .modal-button {
    width: 100%;
  }
}
</style>
